import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./VoterRoll.module.styl";

const PAGE_SIZE = 50;

const formatStatus = (value) => {
  const status = String(value || "").toLowerCase();
  if (!status) return "—";

  const key = `voterRoll.status.${status}`;
  const translated = t(key);
  return translated === key ? String(value) : translated;
};

const quoteRsqlValue = (value) => {
  if (typeof value === "number") {
    return String(value);
  }

  return `"${String(value).replaceAll('"', '\\"')}"`;
};

const rsqlIn = (selector, values) =>
  `${selector}=in=(${values.map(quoteRsqlValue).join(",")})`;

const rsqlAnd = (parts) =>
  parts.map((part) => String(part || "").trim()).filter(Boolean).join(";");

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  return String(value).slice(0, 10);
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  return String(value).replace("T", " ").slice(0, 19);
};

const statusClassName = (value) => {
  const status = String(value || "").toUpperCase();

  if (status === "OPEN" || status === "READY") {
    return `${styles.statusBadge} ${styles.statusReady}`;
  }

  if (status === "GENERATING") {
    return `${styles.statusBadge} ${styles.statusWorking}`;
  }

  if (status === "FAILED") {
    return `${styles.statusBadge} ${styles.statusFailed}`;
  }

  return styles.statusBadge;
};

const loadEventsById = async (rolls) => {
  const ids = Array.from(
    new Set(
      (rolls || [])
        .map((roll) => Number(roll?.electoralEventId))
        .filter((id) => Number.isFinite(id))
    )
  );

  if (!ids.length) {
    return new Map();
  }

  const events = await rest.request(
    getServiceUri() + "electoral-event/search",
    "POST",
    {
      columns: [],
      query: rsqlIn("id", ids),
      limit: ids.length,
      offset: 0
    }
  );

  return new Map(
    (events || []).map((event) => [Number(event.id), event])
  );
};

const buildSearchClause = (search) => {
  const value = String(search || "").trim();

  if (!value) {
    return "";
  }

  if (/^\d+$/.test(value)) {
    return `(id==${Number(value)},electoralEventId==${Number(value)})`;
  }

  const escaped = value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  return `statusCode=re="(?i).*${escaped}.*"`;
};

const buildSort = (sort) => {
  const out = [];

  for (const item of sort || []) {
    if (item?.direction !== "asc" && item?.direction !== "desc") {
      continue;
    }

    const selector = item?.column?.sortSelector || item?.field;

    if (selector) {
      out.push({ selector, direction: item.direction });
    }
  }

  return out.length
    ? out
    : [{ selector: "createdAt", direction: "desc" }];
};

const createProjection = (typeCode, routeSegment, gui) => ({
  storageKey: `haiti.voter-roll.${routeSegment}`,

  columns: [
    {
      title: "voterRoll.event",
      field: "eventName",
      sorting: false,
      render: (row) => (
        <div className={styles.eventCell}>
          <div className={styles.eventName}>
            {row.eventName || `#${row.electoralEventId}`}
          </div>
          <div className={styles.secondaryText}>
            {row.eventDate ? formatDate(row.eventDate) : ""}
          </div>
        </div>
      )
    },
    {
      title: "common.status",
      field: "statusCode",
      sortSelector: "statusCode",
      render: (row) => (
        <span className={statusClassName(row.statusCode)}>
          {formatStatus(row.statusCode)}
        </span>
      )
    },
    {
      title: "voterRoll.cutoff",
      field: "cutoffAt",
      sortSelector: "cutoffAt",
      render: (row) => formatDateTime(row.cutoffAt)
    },
    {
      title: "voterRoll.createdAt",
      field: "createdAt",
      sortSelector: "createdAt",
      render: (row) => formatDateTime(row.createdAt)
    },
    {
      title: "voterRoll.closedAt",
      field: "closedAt",
      sortSelector: "closedAt",
      render: (row) => formatDateTime(row.closedAt)
    },
    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: (row) => (
    can(gui, "READ_VOTER_ROLL") ? (
      <Tooltip title={t("voterRoll.viewRoll")}>
      <IconButton
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          gui.goTo(`/voter-roll/${routeSegment}/${row.id}`);
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      </Tooltip>
    ) : null
  ),

  fetch: async (query) => {
    const page = Number.isFinite(query?.page) ? query.page : 0;
    const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : PAGE_SIZE;
    const offset = page * pageSize;
    const searchClause = buildSearchClause(query?.search);
    const rsql = rsqlAnd([
      `typeCode==${quoteRsqlValue(typeCode)}`,
      searchClause
    ]);

    const payload = {
      columns: [],
      query: rsql,
      limit: pageSize,
      offset,
      sort: buildSort(query?.sort)
    };

    const [rolls, totalCount] = await Promise.all([
      rest.request(
        getServiceUri() + "voter-roll/search",
        "POST",
        payload
      ),
      rest.request(
        getServiceUri() + "voter-roll/count",
        "POST",
        {
          columns: [],
          query: rsql,
          limit: null,
          offset: null
        }
      )
    ]);

    const eventsById = can(gui, "SEARCH_ELECTORAL_EVENT")
      ? await loadEventsById(rolls || [])
      : new Map();

    const data = (rolls || []).map((roll) => {
      const electoralEvent = eventsById.get(Number(roll.electoralEventId));

      return {
        ...roll,
        eventName: electoralEvent?.name || "",
        eventDate: electoralEvent?.eventDate || null
      };
    });

    return {
      data,
      totalCount: Number(totalCount || 0),
      page
    };
  }
});

export const displayPreliminaryRolls = (gui) =>
  displayList(gui, createProjection("PRELIMINARY", "preliminary", gui));

export const displayFinalRolls = (gui) =>
  displayList(gui, createProjection("FINAL", "final", gui));

export default displayPreliminaryRolls;
